import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import StripeCheckout from '../../Components/StripeCheckout';
import TossCheckout from '../../Components/TossCheckout';
import XenditCheckout from '../../Components/XenditCheckout';
import { CreateXenditIntentParams, Product, StripeIntent, TossIntent, XenditIntent } from 'ai-api/types/purchase';
import PurchaseService from '../../Data/PurchaseService';
import { navigateTo } from '@/Utils/routing';
import { formatPrice } from '../../Utils/formatting';
import './link.scss';
import { useSession } from '@/Recoil/Session/Session';

const test_PK = 'pk_test_51RMJLQEHF2kyRI5HVC7XZHF9pCHGavOQo3e1EvgYA6Rhs8bNs10cAqdnhwFLZ8JgvBc3WuOAYlyNHeSLbysCXt1M00Ov1Ziv5i';
const real_PK = 'pk_live_51RMJLQEHF2kyRI5HOvQxhGSrZPtxPzoQKYIXXMaYFSY3JuJJ5Q4bJRwKrWJ5Ww2C9NTHVeMucSPe7CxDbc9dqDCS00LZME45Uw';

const origin = typeof window !== 'undefined' ? window.location.hostname : '';
const isProduction = origin === 'koreanmate.app';
const stripePromise = loadStripe(isProduction ? real_PK : test_PK);

type PaymentMethod = 'stripe' | 'toss' | 'xendit';

interface CustomerInfo {
    name: string;
    email: string;
    phoneNumber: string;
}

const isTossIntent = (data: any): data is TossIntent => {
    return data && typeof data.clientKey === 'string' && typeof data.orderid === 'string' &&
           typeof data.orderName === 'string' && typeof data.amount === 'number' &&
           typeof data.currency === 'string' && typeof data.successUrl === 'string' &&
           typeof data.failUrl === 'string';
}

// Extended type to include qrImage from API response
type XenditIntentWithQrImage = XenditIntent & {
    qrImage?: string;
}

const isXenditIntent = (data: any): data is XenditIntentWithQrImage => {
    return data && typeof data.orderid === 'string' &&
           typeof data.orderName === 'string' && typeof data.amount === 'number' &&
           typeof data.currency === 'string' && typeof data.channelCode === 'string';
}

const PaymentLink: React.FC = () => {
    // Get productid from URL query params using native browser API
    const urlParams = new URLSearchParams(window.location.search);
    const productId = urlParams.get('productid');
    
    const [loading, setLoading] = useState(true);
    const [product, setProduct] = useState<Product | null>(null);

    const {session, sessionRefresh} = useSession();

    useEffect(() => {
        sessionRefresh();
    }, []);

    const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
        name: session.name || '',
        email: session.email || '',
        phoneNumber: ''
    });
    useEffect(() => {
        setCustomerInfo({
            name: session.name || '',
            email: session.email || '',
            phoneNumber: customerInfo.phoneNumber // Preserve existing phone number
        });
    }, [session.name, session.email]);

    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('stripe');
    const [showPayment, setShowPayment] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [tossPaymentData, setTossPaymentData] = useState<TossIntent|StripeIntent|null>(null);
    const [xenditPaymentData, setXenditPaymentData] = useState<XenditIntentWithQrImage|null>(null);
    const [xenditPaymentMethod, setXenditPaymentMethod] = useState<string>('QRIS');
    const [errors, setErrors] = useState<Partial<CustomerInfo>>({});
    const [quantity, setQuantity] = useState(1);

    useEffect(() => {
        const fetchProduct = async (id: string) => {
            try {
                const response = await PurchaseService.getProduct(id);
                const productData = response.data || response;
                // Ensure repeatable field exists (default to true for backwards compatibility)
                if (productData && productData.repeatable === undefined) {
                    productData.repeatable = true;
                }
                setProduct(productData);
            } catch (error) {
                console.error('Failed to fetch product:', error);
            } finally {
                setLoading(false);
            }
        };

        if (productId) {
            fetchProduct(productId);
        } else {
            setLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        // Auto-select payment method based on currency
        if (product?.currency === 'KRW') {
            setPaymentMethod('toss');
        } else if (product?.currency === 'IDR') {
            setPaymentMethod('xendit');
        } else if (product?.currency === 'USD') {
            setPaymentMethod('stripe');
        }
    }, [product?.currency]);

    const validateForm = (): boolean => {
        const newErrors: Partial<CustomerInfo> = {};
        
        if (!customerInfo.name.trim()) {
            newErrors.name = "Name is required";
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(customerInfo.email)) {
            newErrors.email = "Please enter a valid email";
        }
        
        const phoneRegex = /^[\d\s\-+()]+$/;
        if (!phoneRegex.test(customerInfo.phoneNumber) || customerInfo.phoneNumber.length < 10) {
            newErrors.phoneNumber = "Please enter a valid phone number";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleProceedToPayment = async () => {
        if (!validateForm()) {
            return;
        }

        if (paymentMethod === 'stripe') {
            try {
                const response = await PurchaseService.createStripeIntent(
                    productId!,
                    customerInfo.email,
                    customerInfo.name,
                    quantity
                );
                setClientSecret(response.data.clientSecret);
                setShowPayment(true);
            } catch (error) {
                console.error('Failed to create payment intent:', error);
            }
        } else if (paymentMethod === 'toss') {
            handleTossPayment();
        } else if (paymentMethod === 'xendit') {
            handleXenditPayment();
        }
    };

    const handleTossPayment = async () => {
        try {
            const successUrl = `${window.location.origin}/payment/success`;
            const failUrl = `${window.location.origin}/payment/failed`;
            
            const response = await PurchaseService.createTossIntent(
                productId!,
                successUrl,
                failUrl,
                quantity
            );
            
            if (response?.data) {
                setTossPaymentData(response.data);
                setShowPayment(true);
            }
        } catch (error) {
            console.error('Failed to create Toss payment intent:', error);
        }
    };

    const handleXenditPayment = async () => {
        setLoading(true);
        try {
            const successUrl = `${window.location.origin}/payment/success`;
            const failUrl = `${window.location.origin}/payment/failed`;
            
            const response = await PurchaseService.createXenditIntent({
                productid: productId!,
                successUrl,
                failUrl,
                paymentMethod: xenditPaymentMethod as CreateXenditIntentParams['paymentMethod'],
                currency: product?.currency || 'IDR',
                email: customerInfo.email,
                name: customerInfo.name,
                quantity
            });
            
            if (response?.data) {
                setXenditPaymentData(response.data);
                setShowPayment(true);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Failed to create Xendit payment intent:', error);
        }
    };

    const handlePaymentSuccess = () => {
        // Navigate using custom SPA routing
        navigateTo('/payment/success');
    };

    const handlePaymentCancel = () => {
        setShowPayment(false);
        setClientSecret(null);
        setTossPaymentData(null);
        setXenditPaymentData(null);
    };

    if (loading) {
        return <div className="payment-link-loading">{"Loading..."}</div>;
    }

    if (!productId || !product) {
        return <div className="payment-link-error">{"Invalid product ID"}</div>;
    }

    return (
        <div className="payment-link-container">
            <div className="payment-link-content">
                <h2 className='title'>{"Complete Your Purchase"}</h2>
                
                <div className="section">
                    <h2 className="section-title">{"Order Summary"}</h2>
                    <div className="section-content">
                        <div className="product-summary">
                            <div className="product-name">{product.name}</div>
                            <div className="price-display">
                                {product.amount !== product.finalAmount && (
                                    <span className="original-price">
                                        {formatPrice(product.amount * quantity, product?.currency)}
                                    </span>
                                )}
                                <span className="final-price">
                                    {formatPrice(product.finalAmount * quantity, product?.currency)}
                                </span>
                            </div>
                            {product?.detail && (
                                <p className="product-detail">{product.detail}</p>
                            )}
                        </div>
                    </div>
                </div>
                {!showPayment ? (
                    <>
                        {product?.repeatable ? (
                            <div className="section">
                                <h2 className="section-title">{"Quantity"}</h2>
                                <div className="section-content">
                                    <div className="quantity-controls">
                                    <button 
                                        className="quantity-btn"
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        disabled={quantity <= 1}
                                    >
                                        {"-"}
                                    </button>
                                    <input
                                        type="number"
                                        className="quantity-input"
                                        value={quantity}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value) || 1;
                                            setQuantity(Math.max(1, Math.min(99, val)));
                                        }}
                                        min="1"
                                        max="99"
                                    />
                                    <button 
                                        className="quantity-btn"
                                        onClick={() => setQuantity(Math.min(99, quantity + 1))}
                                        disabled={quantity >= 99}
                                    >
                                        {"+"}
                                    </button>
                                    </div>
                                    <div className="quantity-total">
                                        {"Total: "}{formatPrice(product.finalAmount * quantity, product?.currency)}
                                    </div>
                                </div>
                            </div>
                        ) : <></> }
                        <div className="section">
                            <h2 className="section-title">{"Customer Information"}</h2>
                            <div className="section-content">
                                <div className="form-group">
                                <label htmlFor="name">{"Name *"}</label>
                                <input
                                    id="name"
                                    type="text"
                                    value={customerInfo.name}
                                    onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                                    className={errors.name ? 'error' : ''}
                                />
                                {errors.name && <span className="error-message">{errors.name}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">{"Email *"}</label>
                                <input
                                    id="email"
                                    type="email"
                                    value={customerInfo.email}
                                    onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                                    className={errors.email ? 'error' : ''}
                                />
                                {errors.email && <span className="error-message">{errors.email}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="phone">{"Phone Number *"}</label>
                                <input
                                    id="phone"
                                    type="tel"
                                    value={customerInfo.phoneNumber}
                                    onChange={(e) => setCustomerInfo({...customerInfo, phoneNumber: e.target.value})}
                                    className={errors.phoneNumber ? 'error' : ''}
                                />
                                {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
                                </div>
                            </div>
                        </div>

                        <div className="section">
                            <h2 className="section-title">{"Payment Method"}</h2>
                            <div className="section-content">
                                <div className="payment-options">
                                {product?.currency === 'KRW' ? (
                                    <label className="payment-option selected">
                                        <input
                                            type="radio"
                                            value="toss"
                                            checked={true}
                                            readOnly
                                        />
                                        <span>{"Toss Payments (Korean Cards)"}</span>
                                    </label>
                                ) : product?.currency === 'IDR' ? (
                                    <>
                                        <label className="payment-option selected">
                                            <input
                                                type="radio"
                                                value="xendit"
                                                checked={true}
                                                readOnly
                                            />
                                            <span>{"Indonesian Payment Methods"}</span>
                                        </label>
                                        <div className="xendit-method-selector">
                                            <select 
                                                value={xenditPaymentMethod}
                                                disabled
                                                onChange={(e) => setXenditPaymentMethod(e.target.value)}
                                                className="xendit-select"
                                            >
                                                <option value="QRIS">QRIS (QR Payment)</option>
                                                <option value="OVO">OVO E-Wallet</option>
                                                <option value="SHOPEEPAY">ShopeePay</option>
                                            </select>
                                        </div>
                                    </>
                                ) : product?.currency === 'USD' ? (
                                    <label className="payment-option selected">
                                        <input
                                            type="radio"
                                            value="stripe"
                                            checked={true}
                                            readOnly
                                        />
                                        <span>{"Credit/Debit Card (Stripe)"}</span>
                                    </label>
                                ) : (
                                    <>
                                        <label className={`payment-option ${paymentMethod === 'stripe' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                value="stripe"
                                                checked={paymentMethod === 'stripe'}
                                                onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                                            />
                                            <span>{"Credit/Debit Card (Stripe)"}</span>
                                        </label>
                                        <label className={`payment-option ${paymentMethod === 'toss' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                value="toss"
                                                checked={paymentMethod === 'toss'}
                                                onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                                            />
                                            <span>{"Toss Payments (Korean Cards)"}</span>
                                        </label>
                                        <label className={`payment-option ${paymentMethod === 'xendit' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                value="xendit"
                                                checked={paymentMethod === 'xendit'}
                                                onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                                            />
                                            <span>{"Indonesian Payment Methods"}</span>
                                        </label>
                                    </>
                                )}
                                </div>
                                {paymentMethod === 'xendit' && product?.currency !== 'IDR' && (
                                    <div className="xendit-method-selector">
                                        <select 
                                            value={xenditPaymentMethod}
                                            onChange={(e) => setXenditPaymentMethod(e.target.value)}
                                            className="xendit-select"
                                        >
                                            <option value="QRIS">QRIS (QR Payment)</option>
                                            <option value="DANA">DANA E-Wallet</option>
                                            <option value="OVO">OVO E-Wallet</option>
                                            <option value="LINKAJA">LinkAja E-Wallet</option>
                                            <option value="SHOPEEPAY">ShopeePay</option>
                                            <option value="BRI_VIRTUAL_ACCOUNT">BRI Virtual Account</option>
                                            <option value="BNI_VIRTUAL_ACCOUNT">BNI Virtual Account</option>
                                            <option value="MANDIRI_VIRTUAL_ACCOUNT">Mandiri Virtual Account</option>
                                            <option value="PERMATA_VIRTUAL_ACCOUNT">Permata Virtual Account</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="section-action">
                            <button 
                                className="proceed-button"
                                onClick={handleProceedToPayment}
                            >
                                {"Proceed to Payment"}
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="payment-form-wrapper">
                        {paymentMethod === 'stripe' && clientSecret ? (
                            <Elements stripe={stripePromise}>
                                <StripeCheckout
                                    price={product.finalAmount * quantity}
                                    productName={`${product.name}${quantity > 1 ? ` x${quantity}` : ''}`}
                                    clientSecret={clientSecret}
                                    currency={product?.currency || 'USD'}
                                    onSuccess={handlePaymentSuccess}
                                    onCancel={handlePaymentCancel}
                                />
                            </Elements>
                        ) : paymentMethod === 'toss' && isTossIntent(tossPaymentData) ? (
                            <TossCheckout
                                clientKey={tossPaymentData.clientKey}
                                orderid={tossPaymentData.orderid}
                                orderName={tossPaymentData.orderName}
                                amount={tossPaymentData.amount}
                                currency={tossPaymentData.currency}
                                customerName={customerInfo.name}
                                customerEmail={customerInfo.email}
                                customerPhone={customerInfo.phoneNumber}
                                successUrl={tossPaymentData.successUrl}
                                failUrl={tossPaymentData.failUrl}
                                isTestMode={false}
                                onCancel={handlePaymentCancel}
                            />
                        ) : paymentMethod === 'xendit' && isXenditIntent(xenditPaymentData) ? (
                            <XenditCheckout
                                paymentRequestId={xenditPaymentData.paymentRequestId}
                                orderid={xenditPaymentData.orderid}
                                productid={productId}
                                orderName={xenditPaymentData.orderName}
                                amount={xenditPaymentData.amount}
                                currency={xenditPaymentData.currency}
                                channelCode={xenditPaymentData.channelCode}
                                status={xenditPaymentData.status}
                                customerEmail={xenditPaymentData.customerEmail}
                                customerName={xenditPaymentData.customerName}
                                successUrl={xenditPaymentData.successUrl}
                                failUrl={xenditPaymentData.failUrl}
                                clientKey={xenditPaymentData.clientKey}
                                checkoutUrl={xenditPaymentData.checkoutUrl}
                                qrCode={xenditPaymentData.qrCode}
                                qrImage={xenditPaymentData.qrImage}
                                virtualAccountNumber={xenditPaymentData.virtualAccountNumber}
                                isTestMode={!isProduction}
                                onCancel={handlePaymentCancel}
                            />
                        ) : (
                            <div className="payment-loading">{"Initializing payment..."}</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentLink;